import { Phone, ClipboardCheck, Wrench, ThumbsUp } from "lucide-react";

/*
 * LocationProcess — Section 5/10 for /garage-door-repair-northern-beaches/
 * MASTER TEMPLATE — duplicate for other locations, change only the LOC block.
 * Paste this ENTIRE file into src/pages/GarageDoorRepairNorthernBeaches/sections/LocationProcess.tsx
 */

// ╔══════════════════════════════════════════════════════════════════╗
// ║  LOC CONFIG — change ONLY this block when duplicating          ║
// ╚══════════════════════════════════════════════════════════════════╝
const LOC = {
  name: "Northern Beaches",
  h2Span: "on the Northern Beaches",
  subtitle: "From your call to a fixed door — here\'s exactly how it works when you book a job on the Northern Beaches.",
  steps: [{
    icon: "Phone",
    time: "Minute 0",
    title: "You Call or Message",
    desc: "Call 0485 000 933 or WhatsApp us. Tell us the suburb, the door type and what\'s happening. We\'ll give you a fixed-price quote on the spot — no surprises."
  }, {
    icon: "ClipboardCheck",
    time: "Minute 15",
    title: "We Confirm & Dispatch",
    desc: "Same-day booking confirmed. Our van — fully stocked with springs, cables, motors and marine-grade hardware — heads to the Northern Beaches."
  }, {
    icon: "Wrench",
    time: "Minute 70",
    title: "On-Site Fix",
    desc: "We arrive, diagnose, and fix. Most repairs are done within the hour. We factor in Spit Bridge and Warringah Road traffic to keep our ETAs honest."
  }, {
    icon: "ThumbsUp",
    time: "Done",
    title: "Tested, Warranted, Done",
    desc: "Full safety test, clean up, and a 1-Year Labour Warranty certificate. Your door works like new — guaranteed."
  }]
};
// ╚══════════════════════════════════════════════════════════════════╝

const ICONS: Record<string, any> = {
  Phone,
  ClipboardCheck,
  Wrench,
  ThumbsUp
};
const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
export default function LocationProcess() {
  return <section id="location-process" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from LocationBrands bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill="#FAF7F1" />
        </svg>
      </div>

      {/* Grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="lp-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#lp-grain)" />
      </svg>

      {/* Scanlines */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />

      {/* GG watermark */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "280px",
      fontWeight: 900,
      color: `${GOLD}08`,
      fontFamily: "Plus Jakarta Sans,sans-serif",
      pointerEvents: "none",
      zIndex: 1,
      userSelect: "none",
      letterSpacing: "-0.05em"
    }}>
        GG
      </div>

      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Header — centered */}
        <div style={{
        textAlign: "center",
        marginBottom: "52px"
      }}>
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px"
        }}>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: `${GOLD}BB`
          }}>
              How It Works
            </span>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
          </div>
          <h2 style={{
          fontFamily: "Plus Jakarta Sans,sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px,3.5vw,42px)",
          lineHeight: 1.1,
          color: "#FFFFFF",
          marginBottom: "12px"
        }}>
            4 Steps to a Fixed Door<br />
            <span style={{
            color: GOLD
          }}>{LOC.h2Span}</span>
          </h2>
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(14px,1.5vw,16px)",
          color: "rgba(255,255,255,0.6)",
          lineHeight: 1.7,
          maxWidth: "540px",
          margin: "0 auto"
        }}>
            {LOC.subtitle}
          </p>
        </div>

        {/* Steps — vertical timeline */}
        <div style={{
        maxWidth: "700px",
        margin: "0 auto",
        position: "relative"
      }}>

          {/* Vertical line */}
          <div aria-hidden="true" style={{
          position: "absolute",
          left: "23px",
          top: "0",
          bottom: "0",
          width: "2px",
          background: `linear-gradient(180deg,${GOLD}40,${GOLD}15)`
        }} className="max-lg:!left-[23px] lg:!left-[23px]" />

          <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "32px"
        }}>
            {LOC.steps.map((step, i) => {
            const Icon = ICONS[step.icon];
            return <div key={i} style={{
              display: "flex",
              gap: "20px",
              position: "relative"
            }}>

                  {/* Icon circle */}
                  {/** @preserve lucide-do-not-replace */}
                  <div data-icon-container="lucide-do-not-replace" style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: NAVY2,
                border: `2px solid ${GOLD}60`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                zIndex: 2
              }}>
                    {Icon && <Icon size={20} color={GOLD} strokeWidth={2} />}
                  </div>

                  {/* Content */}
                  <div style={{
                background: NAVY2,
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "12px",
                padding: "24px",
                flex: 1
              }}>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "10px",
                  flexWrap: "wrap"
                }}>
                      <span style={{
                    fontFamily: "Manrope,sans-serif",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: GOLD,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    background: `${GOLD}18`,
                    border: `1px solid ${GOLD}30`,
                    borderRadius: "999px",
                    padding: "3px 10px"
                  }}>
                        {step.time}
                      </span>
                      <h3 style={{
                    fontFamily: "Plus Jakarta Sans,sans-serif",
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#FFFFFF",
                    lineHeight: 1.2
                  }}>
                        {step.title}
                      </h3>
                    </div>
                    <p style={{
                  fontFamily: "Manrope,sans-serif",
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.6)",
                  lineHeight: 1.7
                }}>
                      {step.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in LocationWhyUs top */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}